import { Howl } from 'howler';

class AudioManager {
  static instance;

  playbackToken = 0;
  isRecording = false;
  radioWanted = false;
  radioPausedByMessage = false;
  radioHowl = null;
  messageHowl = null;
  listeners = new Set();

  state = {
    currentUrl: '',
    isRadioPlaying: false,
    isMessagePlaying: false,
    isRecording: false,
    radioWanted: false,
  };

  constructor() {
    if (AudioManager.instance) {
      return AudioManager.instance;
    }
    AudioManager.instance = this;
  }

  static getInstance() {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  getState() {
    return this.state;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  emit() {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  syncState() {
    const isRadioPlaying = !!this.radioHowl?.playing();
    const isMessagePlaying = !!this.messageHowl?.playing();

    this.state = {
      currentUrl: isRadioPlaying
        ? this.radioHowl?._src
        : isMessagePlaying
          ? this.messageHowl?._src
          : null,
      isRadioPlaying,
      isMessagePlaying,
      radioWanted: this.radioWanted,
      isRecording: this.isRecording,
    };

    this.emit();
  }

  playRadio(src) {
    this.radioWanted = true;

    if (this.messageHowl?.playing()) {
      this.stopMessage();
    }

    if (!this.radioHowl) {
      this.radioHowl = new Howl({
        src: [src],
        html5: true,
        format: ['mp3', 'wav', 'ogg'],
        autoplay: false,
        volume: 1,
        // Optimisations pour les streams
        preload: false, // Pas de preload pour les streams
        buffer: true, // Buffer activé pour les streams
        xhr: {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        },
        onplay: () => {
          this.state = {
            ...this.state,
            currentUrl: src,
            isMessagePlaying: false,
            isRadioPlaying: true,
            radioWanted: true,
          };
          this.emit();
        },
        onpause: () => this.syncState(),
        onstop: () => this.syncState(),
        onend: () => this.syncState(),
      });
    }

    if (!this.radioHowl.playing()) {
      this.radioHowl.play();
    }

    this.syncState();
  }

  pauseRadio() {
    this.radioHowl?.pause();
    this.syncState();
  }

  resumeRadio() {
    if (!this.isRecording && this.radioWanted && this.radioPausedByMessage) {
      this.radioPausedByMessage = false;
      this.radioHowl?.play();
    }
  }

  stopRadio() {
    this.radioWanted = false;
    this.radioPausedByMessage = false;
    this.radioHowl?.stop();
    this.syncState();
  }

  playMessage(url) {
    const token = ++this.playbackToken;

    if (this.messageHowl) {
      this.messageHowl.off();
      this.messageHowl.stop();
      this.messageHowl.unload();
      this.messageHowl = null;
    }

    if (this.radioHowl?.playing()) {
      this.radioPausedByMessage = true;
      this.pauseRadio();
    } else {
      this.radioPausedByMessage = false;
    }

    const howl = new Howl({
      src: [url],
      html5: true,
      format: ['mp3', 'wav', 'ogg'],
      autoplay: false,
      volume: 1,
      preload: true,
      onplay: () => {
        this.state = {
          ...this.state,
          currentUrl: url,
          isMessagePlaying: true,
          isRadioPlaying: false,
          radioWanted: this.radioWanted,
        };
        this.emit();
      },
      onpause: () => this.syncState(),
      onstop: () => this.syncState(),
      onend: () => {
        if (token !== this.playbackToken) return;

        this.messageHowl?.off();
        this.messageHowl?.unload();
        this.messageHowl = null;
        this.resumeRadio();
        this.syncState();
      },
    });

    this.messageHowl = howl;
    howl.play();
    this.state = {
      ...this.state,
      currentUrl: url,
      isMessagePlaying: true,
      isRadioPlaying: false,
      radioWanted: this.radioWanted,
    };
    this.emit();
  }

  stopMessage() {
    ++this.playbackToken;

    if (this.messageHowl) {
      this.messageHowl.off();
      this.messageHowl.stop();
      this.messageHowl.unload();
      this.messageHowl = null;
    }

    this.resumeRadio();
    this.syncState();
  }

  toggleMessage(url) {
    const current = this.state.currentUrl;

    if (current === url && this.messageHowl?.playing()) {
      this.stopMessage();
      return;
    }

    this.playMessage(url);
  }

  beforeRecordingStart() {
    this.isRecording = true;
    if (this.messageHowl?.playing()) {
      this.stopMessage();
    }
    if (this.radioHowl?.playing()) {
      this.radioPausedByMessage = true;
      this.pauseRadio();
    }
  }

  afterRecordingStop() {
    this.isRecording = false;
    this.resumeRadio();
  }
}

export default AudioManager.getInstance();
